// CheckoutForm.js
// eslint-disable-next-line max-classes-per-file
import PropTypes from 'prop-types';
import React from 'react';
import { Col, FormControl, FormGroup, Row } from 'react-bootstrap/lib/';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardCVCElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  ElementsConsumer,
} from '@stripe/react-stripe-js';
import { COUNTRIES, STATES_BY_COUNTRY } from 'Constants/Common';
import { FloatingInput } from '@piplapps/pipl-ui';

import { getInputValue } from '../../utils';
import './CreditCardForm.less';
import AppConfig from '../../../../Config/AppConfig';

class CreditCardForm extends React.Component {
  static stripeInputStyle = {
    base: {
      fontSize: '16px',
      color: '#000',
      lineHeight: '42px',
      fontFamily: "'Open Sans', tahoma, arial, sans-serif",
      '::placeholder': {
        color: '#9b9b9b',
      },
    },
    invalid: {
      color: 'red',
    },
  };

  // eslint-disable-next-line react/static-property-placement
  static propTypes = {
    onSubmit: PropTypes.func,
    createStripeTokenInProgress: PropTypes.func.isRequired,
    createStripeTokenFailed: PropTypes.func,
    organization: PropTypes.object,
    stripe: PropTypes.object,
    onCreditCardUpdate: PropTypes.func,
    form: PropTypes.object,
    updatedState: PropTypes.func,
  };

  constructor(props) {
    super(props);
    // eslint-disable-next-line react/state-in-constructor
    this.state = {
      form: {
        country: this.props.organization.country_code || '',
        name: '',
        city: this.props.organization.city || '',
        state: this.props.organization.state || '',
        address: this.props.organization.address || '',
        postalCode: this.props.organization.postal_code || '',
        ...(props.form || {}),
      },
      name: {
        validationState: null,
        help: null,
      },
      address: {
        validationState: this.props.organization.address ? 'success' : null,
        help: null,
      },
      city: {
        validationState: this.props.organization.city ? 'success' : null,
        help: null,
      },
      state: {
        validationState: this.props.organization.state ? 'success' : null,
        help: null,
      },
      country: {
        validationState: this.props.organization.country_code ? 'success' : null,
        help: null,
      },
      postalCode: {
        validationState: this.props.organization.postal_code ? 'success' : null,
        help: null,
      },
      cardCvc: {
        isEmpty: true,
        validationState: null,
        help: null,
      },
      cardNumber: {
        isEmpty: true,
        validationState: null,
        help: null,
      },
      cardExpiry: {
        isEmpty: true,
        validationState: null,
        help: null,
      },
    };
  }

  componentDidMount() {
    if (Object.keys(this.props.form).length > 0) this.validateForm();
  }

  _isFormValid = () => {
    const requiredValidations = [
      'name',
      'city',
      'country',
      'address',
      'postalCode',
      'cardNumber',
      'cardExpiry',
      'cardCvc',
    ];
    return requiredValidations.every((validation) => this.state[validation].validationState);
  };

  componentDidUpdate() {
    this.props.updatedState(this._isFormValid());
  }

  /* eslint-disable react/no-access-state-in-setstate */
  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    if (Object.keys(nextProps.form).length > 0) {
      this.setState({
        form: {
          ...this.state.form,
          ...nextProps.form,
        },
      });
    }
  }

  setCCState(stateCC) {
    this.setState(stateCC);
  }

  cleanValidation() {
    this.setCCState({
      name: {
        validationState: null,
        help: null,
      },
      address: {
        validationState: null,
        help: null,
      },
      city: {
        validationState: null,
        help: null,
      },
      state: {
        validationState: null,
        help: null,
      },
      country: {
        validationState: null,
        help: null,
      },
      postalCode: {
        validationState: null,
        help: null,
      },
      cardCvc: {
        isEmpty: true,
        validationState: null,
        help: null,
      },
      cardNumber: {
        isEmpty: true,
        validationState: null,
        help: null,
      },
      cardExpiry: {
        isEmpty: true,
        validationState: null,
        help: null,
      },
    });
  }

  handleChange(change) {
    if (change.empty === false && change.error) {
      this.setState({
        [change.elementType]: {
          isEmpty: false,
          validationState: 'error',
          help: change.error.message,
        },
      });
    } else if (change.empty === false && change.complete) {
      this.setState({
        [change.elementType]: {
          isEmpty: false,
          validationState: 'success',
          help: null,
        },
      });
    } else if (change.empty) {
      this.setState({
        [change.elementType]: {
          isEmpty: true,
          ...[change.elementType],
        },
      });
    }
  }

  validateCountryState() {
    const country = getInputValue(this._country);
    const stateVal = this._stateInput ? getInputValue(this._stateInput) : null;

    if (country) {
      if (this.isValidCountryState()) {
        this.setCCState({
          state: { validationState: 'success', help: null },
          country: { validationState: 'success', help: null },
        });
      } else if (country === 'US' || country === 'CA') {
        this.setCCState({
          state: {
            validationState: 'error',
            help: gettext('State is required'),
          },
          country: { validationState: null, help: null },
        });
      } else if (stateVal) {
        this.setCCState({
          state: {
            validationState: 'error',
            help: gettext('Not in this country'),
          },
          country: { validationState: null, help: null },
        });
      }
      this.setState({ form: { country } });
    } else {
      this.setCCState({
        country: {
          validationState: 'error',
          help: gettext('Country is required'),
        },
      });
    }
  }

  validateRequired(field) {
    const value = getInputValue(this[`_${field}`]);
    const newState = {};

    newState[field] =
      value.length > 0
        ? { validationState: 'success', help: null }
        : {
            validationState: 'error',
            help: `credit card ${field} required`,
          };

    this.setCCState(newState);
  }

  validateStripe() {
    if (this.state.cardExpiry.validationState === null) {
      this.setCCState({
        cardExpiry: {
          validationState: 'error',
          help: gettext('Please fill expiry date'),
        },
      });
    }
    if (this.state.cardCvc.validationState === null) {
      this.setCCState({
        cardCvc: {
          validationState: 'error',
          help: gettext('Please fill cvc number'),
        },
      });
    }
    if (this.state.cardNumber.validationState === null) {
      this.setCCState({
        cardNumber: {
          validationState: 'error',
          help: gettext('Please fill credit card number'),
        },
      });
    }
  }

  validateForm() {
    if (!this.isFormValid()) {
      this.validateRequired('name');
      this.validateRequired('address');
      this.validateRequired('city');
      this.validateRequired('postalCode');
      this.validateCountryState();
      this.validateStripe();
      return false;
    }
    return true;
  }

  isValidCountryState() {
    const country = getInputValue(this._country);
    const state = this._stateInput ? getInputValue(this._stateInput) : null;

    if (country === 'US' || country === 'CA') {
      const validStates = STATES_BY_COUNTRY[country];
      if (validStates.findIndex((st) => st[0] === state) > -1) {
        return true;
      }
    } else if (country) {
      return true;
    }
    return false;
  }

  isAddressValid() {
    const address = getInputValue(this._address);
    const city = getInputValue(this._city);
    const postalCode = getInputValue(this._postalCode);

    return address.length > 0 && city.length > 0 && postalCode.length > 0;
  }

  isNameValid() {
    return getInputValue(this._name).length > 0;
  }

  isFormValid() {
    return this.isNameValid() && this.isValidCountryState() && this.isAddressValid();
  }

  submit() {
    if (this.validateForm()) {
      const name = getInputValue(this._name);
      const addressLine1 = getInputValue(this._address);
      const addressCity = getInputValue(this._city);
      const addressCountry = getInputValue(this._country);
      const addressState = this._stateInput ? getInputValue(this._stateInput) : '';
      const addressZip = getInputValue(this._postalCode);

      this.cleanValidation();
      this.props.createStripeTokenInProgress();

      this.props.stripe
        .createToken({
          name,
          address_line1: addressLine1,
          address_city: addressCity,
          address_state: addressState,
          address_zip: addressZip,
          address_country: addressCountry,
        })
        .then(({ token, error }) => {
          if (token && token.id && token.card) {
            if (token.address_line1_check === 'fail') return this.props.createStripeTokenFailed();

            if (token.address_zip_check === 'fail') return this.props.createStripeTokenFailed();

            this.props.onSubmit(token.id, token.card);

            return true;
          }

          this.props.createStripeTokenFailed(error);
          return false;
        });
    }
  }

  updateField(k, v) {
    if (this.props.onCreditCardUpdate) {
      this.props.onCreditCardUpdate({
        [k]: v,
      });
    }
  }

  render() {
    const { state } = this;
    const ccState = this.state;
    const { form } = this.state;
    const { country } = state.form;

    return (
      <div className="c-credit-card-form">
        <div className="c-credit-card-form__cc">
          <form>
            <Row>
              <Col>
                <div className="section-title">
                  <div className="title-seperator" />
                  <span>{gettext('Credit card details')}</span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormGroup bsSize="small" validationState={ccState.name.validationState}>
                  <FloatingInput
                    placeholder={gettext('Name on Card')}
                    name="cc_name"
                    value={getInputValue(this._name)}
                    forcedError={ccState.name.help}
                    renderInput={() => {
                      return (
                        <FormControl
                          type="text"
                          test-id="name-on-card"
                          ref={(input) => (this._name = input)} //eslint-disable-line
                          onChange={(e) => this.updateField('name', e.target.value)}
                          onBlur={this.validateRequired.bind(this, 'name')} //eslint-disable-line
                        />
                      );
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col sm={12} xs={12}>
                <FormGroup bsSize="small" validationState={ccState.cardNumber.validationState} test-id="cc-card">
                  <FloatingInput
                    placeholder={!ccState.cardNumber.isEmpty ? gettext('Card Number') : null}
                    name="cc_number"
                    value={!ccState.cardNumber.isEmpty}
                    forcedError={ccState.cardNumber.help}
                    renderInput={() => {
                      return (
                        <div className="c-credit-card-form--stripe-wrapper">
                          <CardNumberElement
                            onChange={(e) => {
                              return this.handleChange(e);
                            }}
                            options={{ style: CreditCardForm.stripeInputStyle }}
                          />
                        </div>
                      );
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col sm={6} xs={12}>
                <FormGroup bsSize="small" validationState={ccState.cardExpiry.validationState} test-id="cc-exp">
                  <FloatingInput
                    placeholder={!ccState.cardExpiry.isEmpty ? gettext('Expiration date') : null}
                    name="cc_exp"
                    value={!ccState.cardExpiry.isEmpty}
                    forcedError={ccState.cardExpiry.help}
                    renderInput={() => {
                      return (
                        <div className="c-credit-card-form--stripe-wrapper">
                          <CardExpiryElement
                            onChange={(e) => this.handleChange(e)}
                            options={{ style: CreditCardForm.stripeInputStyle }}
                          />
                        </div>
                      );
                    }}
                  />
                </FormGroup>
              </Col>
              <Col sm={6} xs={12}>
                <FormGroup bsSize="small" validationState={ccState.cardCvc.validationState} test-id="cc-cvc">
                  <FloatingInput
                    placeholder={!ccState.cardCvc.isEmpty ? gettext('CVC') : null}
                    name="cc_cvc"
                    value={!ccState.cardCvc.isEmpty}
                    forcedError={ccState.cardCvc.help}
                    renderInput={() => {
                      return (
                        <div className="c-credit-card-form--stripe-wrapper">
                          <CardCVCElement
                            onChange={(e) => this.handleChange(e)}
                            options={{ style: CreditCardForm.stripeInputStyle }}
                          />
                        </div>
                      );
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="section-title">
                  <div className="title-seperator" />
                  <span>{gettext('Address associated with the card')}</span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col sm={12} xs={12}>
                <FormGroup bsSize="small" validationState={ccState.address.validationState}>
                  <FloatingInput
                    placeholder={gettext('Credit card address')}
                    name="cc_address"
                    value={getInputValue(this._address)}
                    forcedError={ccState.address.help}
                    renderInput={() => {
                      return (
                        <FormControl
                          type="text"
                          test-id="cc-address"
                          ref={(input) => (this._address = input)} //eslint-disable-line
                          value={form ? form.address : ''}
                          onChange={(e) => this.updateField('address', e.target.value)}
                          onBlur={this.validateRequired.bind(this, 'address')} //eslint-disable-line
                        />
                      );
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col sm={country === 'US' || country === 'CA' ? 12 : 12} xs={12}>
                <FormGroup bsSize="small" validationState={ccState.country.validationState}>
                  <FloatingInput
                    // placeholder={gettext('Country')}
                    name="cc_country"
                    value={getInputValue(this._country)}
                    forcedError={ccState.country.help}
                    renderInput={() => {
                      return (
                        <FormControl
                          type="text"
                          test-id="cc-country"
                          ref={(input) => (this._country = input)} //eslint-disable-line
                          componentClass="select"
                          value={form ? form.country : ''}
                          onChange={(e) => {
                            this.updateField('country', e.target.value);
                            this.validateCountryState();
                          }}
                        >
                          <option value="" style={{ display: 'none' }}>
                            {gettext('Country')}
                          </option>
                          {COUNTRIES.map((x) => (
                            <option key={x[0]} value={x[0]}>
                              {x[1]}
                            </option>
                          ))}
                        </FormControl>
                      );
                    }}
                  />
                </FormGroup>
              </Col>
              {country === 'US' || country === 'CA' ? (
                <Col sm={12} xs={12}>
                  <FormGroup bsSize="small" validationState={ccState.state.validationState}>
                    <FloatingInput
                      // placeholder={gettext('Country')}
                      name="cc_state"
                      value={getInputValue(this._stateInput)}
                      forcedError={ccState.state.help}
                      renderInput={() => {
                        return (
                          <FormControl
                            type="text"
                            test-id="cc-state"
                            ref={(input) => (this._stateInput = input)} //eslint-disable-line
                            componentClass="select"
                            value={form ? form.state : ''}
                            onChange={(e) => {
                              this.updateField('state', e.target.value);
                              this.validateCountryState();
                            }}
                          >
                            <option value="">{country === 'CA' ? gettext('Province') : gettext('State')}</option>
                            {STATES_BY_COUNTRY[country].map((x) => (
                              <option key={x[0]} value={x[0]}>
                                {x[1]}
                              </option>
                            ))}
                          </FormControl>
                        );
                      }}
                    />
                  </FormGroup>
                </Col>
              ) : null}
              <Col sm={12} xs={12}>
                <FormGroup bsSize="small" validationState={ccState.city.validationState}>
                  <FloatingInput
                    placeholder={gettext('City')}
                    name="cc_city"
                    value={getInputValue(this._city)}
                    forcedError={ccState.city.help}
                    renderInput={() => {
                      return (
                        <FormControl
                          type="text"
                          test-id="cc-city"
                          ref={(input) => (this._city = input)} //eslint-disable-line
                          value={form ? form.city : ''}
                          onChange={(e) => this.updateField('city', e.target.value)}
                          onBlur={this.validateRequired.bind(this, 'city')} //eslint-disable-line
                        />
                      );
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col sm={12} xs={12}>
                <FormGroup bsSize="small" validationState={ccState.postalCode.validationState}>
                  <FloatingInput
                    placeholder={gettext('Postal Code')}
                    name="cc_zip"
                    value={getInputValue(this._postalCode)}
                    forcedError={ccState.postalCode.help}
                    renderInput={() => {
                      return (
                        <FormControl
                          type="text"
                          test-id="cc-zip"
                          ref={(input) => (this._postalCode = input)} //eslint-disable-line
                          value={form ? form.postalCode : ''}
                          onChange={(e) => this.updateField('postalCode', e.target.value)}
                          onBlur={this.validateRequired.bind(this, 'postalCode')} //eslint-disable-line
                        />
                      );
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </form>
        </div>
      </div>
    );
  }
}

const InjectedForm = (props) => (
  // eslint-disable-next-line react/prop-types
  <ElementsConsumer>{({ stripe }) => <CreditCardForm stripe={stripe} {...props} ref={props.ref} />}</ElementsConsumer>
);
const stripePromise = loadStripe(AppConfig.STRIPE_PUBLIC_KEY);
class CreditCardIntegratedForm extends React.Component {
  submit() {
    this._createCreditCard.getWrappedInstance().submit();
  }

  render() {
    return (
      <Elements fonts={[{ cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans' }]} stripe={stripePromise}>
        <InjectedForm
          {...this.props}
          ref={(input) => {
            this._createCreditCard = input;
          }}
        />
      </Elements>
    );
  }
}

export default CreditCardIntegratedForm;
